/**
 * Stages of the bot purchase funnel, in the order a lead goes through them.
 *
 * Kept as a const list rather than inferred from the response so the chart renders the stages in funnel
 * order even when the API returns them in another order.
 */
export const BOT_FUNNEL_STAGES = [
  'ATTEMPT_OPENED',
  'START',
  'OFFER_CATEGORIES_SHOWN',
  'OFFERS_SHOWN',
  'OFFER_SELECTED',
  'PAYMENT_METHOD_SELECTED',
  'EMAIL_REQUESTED',
  'EMAIL_PROVIDED',
  'DOCUMENT_REQUESTED',
  'DOCUMENT_PROVIDED',
  'INVOICE_CREATED',
] as const;

export type BotFunnelStage = (typeof BOT_FUNNEL_STAGES)[number];

/** Producer-facing labels. The enum names mean nothing to whoever reads the dashboard. */
export const BOT_FUNNEL_STAGE_LABELS: Record<BotFunnelStage, string> = {
  ATTEMPT_OPENED: 'Iniciou uma conversa',
  // No longer "abriu o bot": that is the stage above, and most leads reach it without ever sending
  // /start. Naming both of them for opening the bot is what made the funnel unreadable.
  START: 'Enviou /start',
  OFFER_CATEGORIES_SHOWN: 'Viu o menu de categorias',
  OFFERS_SHOWN: 'Viu as ofertas com preço',
  OFFER_SELECTED: 'Escolheu uma oferta',
  PAYMENT_METHOD_SELECTED: 'Escolheu o pagamento',
  EMAIL_REQUESTED: 'Bot pediu o e-mail',
  EMAIL_PROVIDED: 'Informou o e-mail',
  DOCUMENT_REQUESTED: 'Bot pediu o documento',
  DOCUMENT_PROVIDED: 'Informou o documento',
  INVOICE_CREATED: 'Gerou a cobrança',
};

/**
 * Shown next to the label for the stages whose name is not self-explanatory.
 *
 * Only two need one, and both for the same reason: the pair at the top of the funnel sounds like the
 * same event and is not. Everything else says what it counts.
 */
export const BOT_FUNNEL_STAGE_HINTS: Partial<Record<BotFunnelStage, string>> = {
  ATTEMPT_OPENED:
    'Toda interação de uma pessoa nova, ou de quem volta depois de 6 horas sem falar com o bot. É o topo do funil: todas as outras etapas são medidas a partir daqui.',
  START:
    'Somente quem enviou o comando /start. Quem chega por link direto, oferta divulgada ou pedido de entrada em grupo nunca envia — por isso este número é bem menor que o de conversas iniciadas, e não é a base da conversão.',
};

/**
 * Etapas cuja diferença em relação à etapa acima não é desistência, então não rendem linha de perda.
 *
 * `START` é o único caso em que a população é estritamente MENOR que a de cima sem ser um passo abaixo
 * dela: quem envia /start é um subconjunto de quem iniciou conversa, e quem chega por link direto, oferta
 * divulgada ou pedido de entrada em grupo nunca envia. Com as duas etapas vizinhas, a subtração sempre cai
 * no ramo de perda positiva e o funil acusa de desistência gente que nunca teve por onde desistir.
 *
 * As outras etapas opcionais mais abaixo — o menu de categorias, que quem chega por oferta divulgada pula,
 * e os passos de e-mail e documento, que o bot pula quando já tem o dado — quebram a cadeia no sentido
 * oposto: sobra gente na etapa de baixo, a subtração fica negativa e `describeStageDelta` já nomeia isso
 * como caminho alternativo. Por isso elas continuam mostrando a linha e esta lista tem um item só.
 */
export const BOT_FUNNEL_STAGES_WITHOUT_DROP_OFF: readonly BotFunnelStage[] = [
  'START',
];

/**
 * The funnel counts LEADS, and this file says so.
 *
 * `buyers` used to name every one of these counts, and it never meant a purchaser: it counts whoever reached
 * the stage, bought or not. Saying "actually bought" then needed a qualifier - `payingBuyers` - which was the
 * tell. That name cost three corrections on this side alone (a card reading "1 venda a cada N pessoas", a rate
 * documented with the wrong numerator, and a column labelled buyer over a count of leads), and each fix was a
 * label: the next reader of the field errs again.
 *
 * report-api retired the old names in #73, closing Vibxtech/report-api#71: the response carries the lead names
 * only, and the fallbacks this repo used while both sets shipped are gone with them.
 */
export type BotFunnelStageRow = {
  stage: string;
  /** Distinct people who reached the stage. Leads, not purchasers - most of them never buy. */
  leads: number;
  /** Distinct purchase attempts. A lead who retries is one person and several attempts. */
  attempts: number;
  events: number;
  rateFromStart: number;
  /** Relative to the top of the funnel (conversations started). This is the share to show. */
  rateFromTop: number;
  /**
   * Relative to the previous stage in funnel order. NOT capped at 100%: a lead arriving on a promoted
   * offer skips the categories keyboard, so a stage can legitimately sit above its predecessor.
   */
  rateFromPrevious: number;
};

/**
 * Per campaign, measured from the SAME top as the funnel above it.
 *
 * It used to carry only /start figures, and the table therefore apologised for itself in a tooltip. That was
 * true when written and stopped being true with Vibxtech/report-api#61, which added the top-of-funnel
 * columns; the apology outlived the defect and taught producers to distrust a number that had become
 * comparable.
 */
export type BotFunnelOriginRow = {
  origin: string;
  /** Conversations opened under this campaign. Someone returning after six hours opens a second one. */
  attemptsOpened: number;
  /**
   * Distinct people behind those conversations - the denominator of purchaseRate.
   *
   * These do NOT add up to the funnel's own lead count, and that is not a discrepancy: someone who arrives
   * twice through two campaigns is one lead in the headline and one lead in each of two rows here. No
   * dimension of a distinct count can be summed. This is why the tooltip keeps the "não somam" half of what
   * it used to say and drops the "não são comparáveis" half, which report-api#61 made false.
   */
  leads: number;
  /** Distinct people who sent a literal /start under this campaign. A subset of leads. */
  starts: number;
  paidSales: number;
  /** Distinct people with a paid sale under this campaign - the numerator of purchaseRate. */
  purchasers: number;
  /** purchasers / leads. The campaign's conversion, on the same basis as the funnel headline. */
  purchaseRate: number;
  /**
   * paidSales / starts. Kept and uncapped: it answers a real question about the people who typed the
   * command, and its passing 100% is the visible sign that /start is not the top of the funnel.
   */
  startToSaleRate: number;
};

/**
 * Per offer, from impression to sale.
 *
 * Impressions are what makes this readable: the existing offer report shows sales per offer with no
 * denominator, so an offer nobody buys looks the same as one nobody is shown.
 */
export type BotFunnelOfferRow = {
  offerId: number | null;
  /** Null when the offer no longer exists. */
  offerName: string | null;
  impressions: number;
  selected: number;
  paidSales: number;
  /** selected / impressions - is the offer attractive? */
  selectionRate: number;
  /** paidSales / impressions - does it actually sell? */
  impressionToSaleRate: number;
};

/** Per payment method the lead CHOSE in the bot, which is the step before a charge exists. */
export type BotFunnelPaymentMethodRow = {
  paymentMethod: string;
  selected: number;
  paidSales: number;
  /** paidSales / selected, 0 when nobody chose it. */
  selectionToSaleRate: number;
};

/**
 * Per period, on the same top as everything else.
 *
 * Nothing renders this yet. It is typed anyway, and typed on the new denominator, so that whatever renders it
 * cannot be born on /start - which is how a chart of startToSaleRate over time would have moved with the share
 * of traffic that happened to send the command rather than with the funnel.
 */
export type BotFunnelSeriesRow = {
  period: string;
  /** Conversations opened in the period. */
  attemptsOpened: number;
  /** Distinct people behind them - the denominator of purchaseRate. */
  leads: number;
  /** Distinct people who sent a literal /start in the period. */
  starts: number;
  paidSales: number;
  /** Distinct people with a paid sale in the period - the numerator of purchaseRate. */
  purchasers: number;
  /** purchasers / leads. Comparable with the funnel headline. */
  purchaseRate: number;
  /** paidSales / starts. */
  startToSaleRate: number;
};

export type BotFunnelResponse = {
  /**
   * When measurement began for the selected communities, ignoring the selected period. Null when they
   * were never measured.
   *
   * This is what lets the card tell three cases apart instead of two: nothing measured yet, a period
   * PARTIALLY covered by measurement, and a fully covered one. The middle case is the dangerous one -
   * real numbers covering a fraction of the period, sitting next to sales cards covering all of it.
   */
  collectionStartedAt: string | null;
  stages: BotFunnelStageRow[];
  /**
   * The top of the funnel, and the denominator of the conversion: a person's first interaction, or their
   * first after six hours of inactivity.
   */
  attemptsOpened: {
    /** Conversations started in the period. Someone who comes back tomorrow starts a second one. */
    attempts: number;
    /** Distinct people behind them. */
    leads: number;
  };
  /**
   * purchasers / attemptsOpened.LEADS. The conversion rate.
   *
   * NOT paidSales over leads, which is what this comment used to say. paidSales counts invoices, so that
   * reading divides invoices by people and is not a per-person rate at all: it passes 100% as soon as paid
   * invoices outnumber leads. The wrong numerator was corrected in the API contract by
   * Vibxtech/report-api#62 and this copy of it went on asserting the old one - a comment has no compiler
   * behind it, which is the whole reason report-api#71 renames the fields instead of re-documenting them.
   *
   * Per person, not per conversation: someone who comes back after six hours opens a second conversation
   * and is still one person here. Labelling a card built on this "por conversa" would be off by the
   * repeat rate.
   */
  purchaseRate: number;
  /**
   * Distinct people with a paid sale in the period - the numerator of purchaseRate.
   *
   * Lower than paidSales whenever anyone paid twice. paidSales answers how much was sold, this answers
   * how many people bought, and only the second one belongs over a denominator of people.
   */
  purchasers: number;
  /**
   * Inverse of purchaseRate: "one PURCHASER every N people who talked to the bot".
   *
   * Not one SALE every N people, which is what this comment used to say and what a card then printed
   * (fixed in platform-front#257). Both readings are "people per something" and they differ by however
   * often someone buys twice: with 10 leads, 2 purchasers and 3 sales, people per purchaser is 5 and
   * people per sale is 3.3 - so printing 5 as a sale ratio asserts 2 sales where there were 3.
   *
   * The name now says which one it is, which is the fix the label could not be.
   */
  leadsPerPurchaser: number;
  /**
   * Literal /start only. total counts every one, unique counts people.
   *
   * NOT the denominator of anything: most leads reach the bot without sending /start. Kept because
   * "how many typed /start" is still a real question about how people arrive.
   */
  starts: { total: number; unique: number };
  paidSales: number;
  startToSaleRate: number;
  /** Inverse of startToSaleRate: "one sale every N starts", which reads better for a producer. */
  startsPerSale: number;
  byOrigin: BotFunnelOriginRow[];
  byOffer: BotFunnelOfferRow[];
  byPaymentMethod: BotFunnelPaymentMethodRow[];
  series: BotFunnelSeriesRow[];
};

export type BotFunnelFilters = {
  botConfigIds: number[];
  startDate: Date;
  endDate: Date;
  clientTz: string;
  timeAggregationPeriod: string;
};
